import React, { useState, useEffect } from 'react';
import { SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import ResponsiveDetailPanel from '@/components/dashboard/ResponsiveDetailPanel';
import { useIsWideScreen } from '@/hooks/use-media-query';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Phone,
  Mail,
  MapPin,
  Pencil,
  Trash2,
  ArrowRightLeft,
  HardHat,
  Receipt,
  StickyNote,
  Calendar,
  Loader2,
  Sparkles,
  MessageSquare,
  PenLine,
  FileText,
  Send,
  Camera,
  Flag,
  UserPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatUsd,
  getInitials,
  findJobsForContact,
  convertLeadToClient,
  deleteContact,
  setLeadStatus,
  getLeadStatus,
  LEAD_STATUSES,
  LEAD_STATUS_META,
} from '@/lib/clientsData';
import { gatherClientVault, vaultTime } from '@/lib/clientVault';
import { toast } from 'sonner';

// Tone classes per lead status (badge + selected pill).
const STATUS_TONE = {
  won: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40',
  pending:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40',
  learned:
    'bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/40',
};

// Icon per Vault event kind.
const VAULT_ICONS = {
  created: UserPlus,
  status: Flag,
  estimate: FileText,
  sent: Send,
  signature: PenLine,
  message: MessageSquare,
  photo: Camera,
  job: HardHat,
};

const typeStyles = {
  client:
    'bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30',
  lead:
    'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/30',
};

const SectionLabel = ({ children }) => (
  <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-2">
    {children}
  </div>
);

const InfoRow = ({ Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testId}>
    <div className="shrink-0 size-7 rounded-md border border-border bg-[hsl(var(--muted))] flex items-center justify-center text-[hsl(var(--primary))]">
      <Icon size={14} strokeWidth={2} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-foreground break-words">
        {value || <span className="text-muted-foreground/70">—</span>}
      </div>
    </div>
  </div>
);

const JobMiniCard = ({ job }) => {
  const invoices = Array.isArray(job?.invoices) ? job.invoices : [];
  const paid = invoices.filter((i) => i?.status === 'paid').length;

  return (
    <div
      className="rounded-md border border-border bg-[hsl(var(--background))]/40 px-3.5 py-3"
      data-testid={`profile-job-${job.id}`}
    >
      <div className="flex items-center gap-2">
        <HardHat size={14} className="text-muted-foreground shrink-0" />
        <div className="text-sm font-semibold text-foreground truncate flex-1">
          {job.name}
        </div>
        <Badge
          variant="outline"
          className="uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--success))]/12 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
        >
          {job.status || 'active'}
        </Badge>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Contract
          </div>
          <div className="text-sm font-semibold text-foreground">
            {formatUsd(job.contract)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Phase
          </div>
          <div className="text-sm font-semibold text-foreground">
            {job.phase ?? 0} / 5
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Invoices
          </div>
          <div className="text-sm font-semibold text-foreground inline-flex items-center gap-1">
            <Receipt size={12} className="text-muted-foreground" />
            {paid}/{invoices.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ContactProfileSheet = ({
  contact,
  jobs,
  open,
  onOpenChange,
  onEdit,
  onChanged,
  onDeleted,
}) => {
  const isWide = useIsWideScreen();
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savingStatus, setSavingStatus] = useState('');
  const [vaultEvents, setVaultEvents] = useState([]);
  const [vaultLoading, setVaultLoading] = useState(false);

  // Load the Vault timeline when the sheet opens, and refresh after a status
  // change (lead_status_at changes → this re-runs).
  useEffect(() => {
    let alive = true;
    if (!open || !contact?.id) {
      setVaultEvents([]);
      return undefined;
    }
    setVaultLoading(true);
    gatherClientVault(contact, jobs || [])
      .then((evts) => alive && setVaultEvents(evts))
      .catch(() => alive && setVaultEvents([]))
      .finally(() => alive && setVaultLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id, contact?.lead_status_at]);

  if (!contact) return null;
  const isLead = contact.type === 'lead';
  const matchedJobs = findJobsForContact(contact, jobs || []);
  const status = getLeadStatus(contact);

  const handleSetStatus = async (next) => {
    if (getLeadStatus(contact) === next) return;
    setSavingStatus(next);
    try {
      const updated = await setLeadStatus(contact.id, next);
      toast.success(`Marked ${LEAD_STATUS_META[next]?.label || next}`);
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.message || 'Failed to update status.');
    } finally {
      setSavingStatus('');
    }
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const updated = await convertLeadToClient(contact);
      toast.success(`Converted \u2014 ${updated.name} is now a client`);
      onChanged?.(updated);
    } catch (e) {
      toast.error(e?.message || 'Failed to convert lead.');
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteContact(contact.id);
      toast.success(`Deleted \u2014 ${contact.name}`);
      setConfirmDelete(false);
      onOpenChange?.(false);
      onDeleted?.(contact);
    } catch (e) {
      toast.error(e?.message || 'Failed to delete contact.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ResponsiveDetailPanel
      open={open}
      onOpenChange={onOpenChange}
      testId="contact-profile-sheet"
      sheetClassName="sm:max-w-md md:max-w-lg sm:w-[480px]"
    >
        <SheetHeader className="px-5 sm:px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <Sparkles size={14} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {isLead ? 'Lead Profile' : 'Client Profile'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <Avatar className="h-12 w-12 border border-[hsl(var(--primary))]/30">
              <AvatarFallback className="bg-[hsl(var(--muted))] text-[hsl(var(--primary))] text-sm font-semibold">
                {getInitials(contact.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              {isWide ? (
                <h2
                  className="text-foreground tracking-tight text-lg sm:text-xl truncate"
                  data-testid="profile-name"
                >
                  {contact.name}
                </h2>
              ) : (
                <SheetTitle
                  className="text-foreground tracking-tight text-lg sm:text-xl truncate"
                  data-testid="profile-name"
                >
                  {contact.name}
                </SheetTitle>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn('uppercase text-[10px] tracking-[0.16em]', typeStyles[contact.type])}
                  data-testid="profile-type-badge"
                >
                  {isLead ? 'Lead' : 'Client'}
                </Badge>
                {isLead && contact.lead_type ? (
                  <Badge
                    variant="outline"
                    className="uppercase text-[10px] tracking-[0.16em] bg-[hsl(var(--muted))] text-muted-foreground border-border"
                  >
                    {contact.lead_type}
                  </Badge>
                ) : null}
                <Badge
                  variant="outline"
                  className={cn('uppercase text-[10px] tracking-[0.16em]', STATUS_TONE[status])}
                  data-testid="profile-status-badge"
                >
                  {LEAD_STATUS_META[status]?.label || status}
                </Badge>
              </div>
            </div>
          </div>
          {!isWide ? (
            <SheetDescription className="sr-only">
              Contact profile and history
            </SheetDescription>
          ) : null}
        </SheetHeader>

        <ScrollArea className="flex-1 kc-scroll">
          <div className="px-5 sm:px-6 py-5 space-y-6">
            {/* Lead status selector */}
            {isLead ? (
              <section data-testid="lead-status-selector">
                <SectionLabel>Lead Status</SectionLabel>
                <div className="grid grid-cols-3 gap-2">
                  {LEAD_STATUSES.map((s) => {
                    const activeSel = status === s;
                    const saving = savingStatus === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleSetStatus(s)}
                        disabled={!!savingStatus}
                        className={cn(
                          'relative rounded-md border px-2 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors disabled:opacity-60',
                          activeSel
                            ? STATUS_TONE[s]
                            : 'border-border text-muted-foreground hover:bg-[hsl(var(--muted))]'
                        )}
                        data-testid={`lead-status-${s}`}
                      >
                        {saving ? (
                          <Loader2 className="h-3.5 w-3.5 mx-auto animate-spin" />
                        ) : (
                          LEAD_STATUS_META[s]?.label || s
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {/* Contact info */}
            <section>
              <SectionLabel>Contact Info</SectionLabel>
              <div className="divide-y divide-border/60">
                <InfoRow Icon={Phone} label="Phone" value={contact.phone} testId="profile-phone" />
                <InfoRow Icon={Mail} label="Email" value={contact.email} testId="profile-email" />
                <InfoRow Icon={MapPin} label="Address" value={contact.address} testId="profile-address" />
                <InfoRow
                  Icon={Calendar}
                  label="Last contact"
                  value={contact.last_contact}
                  testId="profile-last-contact"
                />
              </div>
            </section>

            {/* Value section */}
            <section className="grid grid-cols-2 gap-3">
              {isLead ? (
                <div className="rounded-md border border-border bg-[hsl(var(--background))]/40 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Estimated value
                  </div>
                  <div
                    className="mt-1 text-lg font-semibold text-[hsl(var(--success))]"
                    data-testid="profile-lead-value"
                  >
                    {formatUsd(contact.lead_value)}
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-border bg-[hsl(var(--background))]/40 p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Lifetime value
                  </div>
                  <div
                    className="mt-1 text-lg font-semibold text-foreground"
                    data-testid="profile-ltv"
                  >
                    {formatUsd(contact.ltv)}
                  </div>
                </div>
              )}
              <div className="rounded-md border border-border bg-[hsl(var(--background))]/40 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Jobs
                </div>
                <div
                  className="mt-1 text-lg font-semibold text-foreground"
                  data-testid="profile-job-count"
                >
                  {matchedJobs.length}
                </div>
              </div>
            </section>

            <Separator className="bg-border" />

            {/* Notes */}
            <section>
              <SectionLabel>Notes</SectionLabel>
              {Array.isArray(contact.notes) && contact.notes.length > 0 ? (
                <ul className="space-y-2" data-testid="profile-notes">
                  {contact.notes.map((note, i) => (
                    <li
                      key={`${i}-${String(note).slice(0, 16)}`}
                      className="flex items-start gap-2.5 rounded-md border border-border bg-[hsl(var(--background))]/40 px-3 py-2.5"
                    >
                      <StickyNote
                        size={14}
                        className="text-[hsl(var(--primary))] mt-0.5 shrink-0"
                      />
                      <span className="text-sm text-foreground/95 leading-snug">{note}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No notes yet.
                </div>
              )}
            </section>

            <Separator className="bg-border" />

            {/* Job History */}
            <section>
              <SectionLabel>Job History</SectionLabel>
              {matchedJobs.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No jobs linked to this contact yet.
                </div>
              ) : (
                <div className="space-y-2" data-testid="profile-jobs">
                  {matchedJobs.map((job) => (
                    <JobMiniCard key={job.id} job={job} />
                  ))}
                </div>
              )}
            </section>

            <Separator className="bg-border" />

            {/* Vault — full activity timeline */}
            <section>
              <SectionLabel>Vault — Activity Timeline</SectionLabel>
              {vaultLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading activity…
                </div>
              ) : vaultEvents.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No activity logged yet.
                </div>
              ) : (
                <ol className="space-y-3" data-testid="client-vault">
                  {vaultEvents.map((ev) => {
                    const Icon = VAULT_ICONS[ev.kind] || StickyNote;
                    return (
                      <li
                        key={ev.id}
                        className="flex items-start gap-3"
                        data-testid={`vault-event-${ev.kind}`}
                      >
                        <div className="shrink-0 size-7 rounded-full border border-border bg-[hsl(var(--muted))] flex items-center justify-center text-[hsl(var(--primary))]">
                          <Icon size={13} strokeWidth={2} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {ev.title}
                            </span>
                            <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">
                              {vaultTime(ev.at)}
                            </span>
                          </div>
                          {ev.detail ? (
                            <div className="text-xs text-muted-foreground truncate">
                              {ev.detail}
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </div>
        </ScrollArea>

        {/* Footer actions */}
        <div className="sticky bottom-0 left-0 right-0 px-5 sm:px-6 py-3 border-t border-border bg-[hsl(var(--card))] flex flex-wrap items-center gap-2">
          {isLead ? (
            <Button
              onClick={handleConvert}
              disabled={converting || deleting}
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="profile-convert"
            >
              {converting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Converting
                </>
              ) : (
                <>
                  <ArrowRightLeft size={14} className="mr-2" />
                  Convert to client
                </>
              )}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            onClick={() => onEdit?.(contact)}
            disabled={converting || deleting}
            className="flex-1 sm:flex-none border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="profile-edit"
          >
            <Pencil size={14} className="mr-2" />
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirmDelete(true)}
            disabled={converting || deleting}
            className="shrink-0 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
            data-testid="profile-delete"
            aria-label="Delete contact"
          >
            <Trash2 size={16} />
          </Button>
        </div>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent
            className="bg-[hsl(var(--card))] border-border"
            data-testid="delete-confirm-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="text-foreground">
                Delete {contact.name}?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">
                This permanently removes the contact from Supabase. Linked jobs are
                NOT deleted, but the client name reference may become orphaned.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
                disabled={deleting}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
                data-testid="delete-confirm-action"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting
                  </>
                ) : (
                  <>Delete</>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </ResponsiveDetailPanel>
  );
};

export default ContactProfileSheet;
